import React, { useContext } from 'react';
import SubHeader from '../../components/subheader/SubHeader';
import './Expenses.scss';
import RevenueBarChart from '../../components/revenue/revenuebarchart/RevenueBarChart';
import DonutChartLine from '../../components/cost/donutchartline/DonutChartLine';
import ExpenseTable from '../../components/expenses/expensetable/ExpenseTable';
import TotalMonthCard from '../../components/totalrevenue/totalmonthcard/TotalMonthCard';
import StackChartExpense from '../../components/expenses/stackchartexpense/StackChartExpense';
import iconExpenseLight from '../../../src/assets/image/expense/Icon-expense-light.svg';
import iconExpenseDark from '../../../src/assets/image/expense/Icon-expense-dark.svg';
import TotalExpense from '../../components/expenses/totalexpense/TotalExpense';
import LineBarExpense from '../../components/expenses/linebarexpense/LineBarExpense';
import ExpenseBarChartHover from '../../components/expenses/expensesbarcharthover/ExpensesBarChartHover';
import { AppContext } from '../../context/Context';
import {
  getQuarterByMonth,
  createRevenueCardYearTitle,
  createRevenueCardQuarterTitle,
  createRevenueCardMonthTitle,
} from '../../utils/format';
import TopVendorsCard from '../../components/expenses/tablevendor/TableVendor';

const Expenses = () => {
  const { data, fromDate, toDate } = useContext(AppContext);

  const allData = data.expenses?.expensesmonth || [];

  const firstRevenueYearMonth =
    allData.length > 0 ? allData[0].year_month : null;

  const lastRevenueYearMonth =
    allData.length > 0 ? allData[allData.length - 1].year_month : null;

  const computeTotalExpensesYear = (fromDate, toDate, data) => {
    let total = 0;

    if (fromDate === 'All' || !toDate) {
      total =
        data.expenses?.expensesmonth?.reduce(
          (sum, item) => sum + (item.expenses || 0),
          0
        ) || 0;
    } else {
      const fromYear = parseInt(fromDate.split('-')[0]);
      const toYear = parseInt(toDate.split('-')[0]);

      total =
        data.expenses?.expensesmonth
          ?.filter((item) => {
            const itemYear = parseInt(item.year_month.split('-')[0]);
            return itemYear >= fromYear && itemYear <= toYear;
          })
          .reduce((sum, item) => sum + (item.expenses || 0), 0) || 0;
    }

    // Format with commas and 2 decimal places
    return total;
  };

  const computeTotalExpensesYearByHoldings = (fromDate, toDate, data) => {
    let total = 0;

    if (fromDate === 'All' || !toDate) {
      total =
        data.expenses?.expensesholdingmonth?.reduce(
          (sum, item) => sum + (item.expenses || 0),
          0
        ) || 0;
    } else {
      const fromYear = parseInt(fromDate.split('-')[0]);
      const toYear = parseInt(toDate.split('-')[0]);

      total =
        data.expenses?.expensesholdingmonth
          ?.filter((item) => {
            const itemYear = parseInt(item.year_month.split('-')[0]);
            return itemYear >= fromYear && itemYear <= toYear;
          })
          .reduce((sum, item) => sum + (item.expenses || 0), 0) || 0;
    }

    // Format with commas and 2 decimal places
    return total;
  };

  const computeTotalExpensesYearByStates = (fromDate, toDate, data) => {
    return (
      computeTotalExpensesYear(fromDate, toDate, data) -
      computeTotalExpensesYearByHoldings(fromDate, toDate, data)
    );
  };

  const computeTotalExpensesQuarter = (fromDate, toDate, data) => {
    if (!data.expenses?.expensesmonth) return 0;

    // Handle "All"
    if (fromDate === 'All' || !toDate) {
      return data.expenses?.expensesmonth.reduce(
        (sum, item) => sum + (item.expenses || 0),
        0
      );
    }

    // --- Parse from/to year & month ---
    const [fromYear, fromMonth] = fromDate.split('-').map(Number);
    const [toYear, toMonth] = toDate.split('-').map(Number);

    const fromQuarter = getQuarterByMonth(fromMonth);
    const toQuarter = getQuarterByMonth(toMonth);

    // --- Filter ---
    const total = data.expenses?.expensesmonth
      .filter((item) => {
        const [itemYear, itemMonth] = item.year_month.split('-').map(Number);
        const itemQuarter = getQuarterByMonth(itemMonth);

        // year before range
        if (itemYear < fromYear || itemYear > toYear) return false;

        // same year as start
        if (itemYear === fromYear && itemQuarter < fromQuarter) return false;

        // same year as end
        if (itemYear === toYear && itemQuarter > toQuarter) return false;

        // in between years → always included
        return true;
      })
      .reduce((sum, item) => sum + (item.expenses || 0), 0);

    return total;
  };

  const computeTotalExpensesQuarterByHoldings = (fromDate, toDate, data) => {
    if (!data.expenses?.expensesholdingmonth) return 0;

    // Handle "All"
    if (fromDate === 'All' || !toDate) {
      return data.expenses?.expensesholdingmonth.reduce(
        (sum, item) => sum + (item.expenses || 0),
        0
      );
    }

    // --- Parse from/to year & month ---
    const [fromYear, fromMonth] = fromDate.split('-').map(Number);
    const [toYear, toMonth] = toDate.split('-').map(Number);

    const fromQuarter = getQuarterByMonth(fromMonth);
    const toQuarter = getQuarterByMonth(toMonth);

    // --- Filter ---
    const total = data.expenses?.expensesholdingmonth
      .filter((item) => {
        const [itemYear, itemMonth] = item.year_month.split('-').map(Number);
        const itemQuarter = getQuarterByMonth(itemMonth);

        // year before range
        if (itemYear < fromYear || itemYear > toYear) return false;

        // same year as start
        if (itemYear === fromYear && itemQuarter < fromQuarter) return false;

        // same year as end
        if (itemYear === toYear && itemQuarter > toQuarter) return false;

        // in between years → always included
        return true;
      })
      .reduce((sum, item) => sum + (item.expenses || 0), 0);

    return total;
  };

  const computeTotalExpensesQuarterByStates = (fromDate, toDate, data) => {
    return (
      computeTotalExpensesQuarter(fromDate, toDate, data) -
      computeTotalExpensesQuarterByHoldings(fromDate, toDate, data)
    );
  };

  const computeTotalExpensesCurrentMonth = (data) => {
    const lines = data.expenses?.expensesmonthfilters;
    if (!Array.isArray(lines)) return '0.00';

    const total = lines.reduce((sum, item) => sum + (item.expenses || 0), 0);

    return total;
  };

  const computeTotalExpensesCurrentMonthByStates = (data) => {
    const lines = data.expenses?.expensesnoneholdingmonth;
    if (!Array.isArray(lines)) return '0.00';

    const total = lines.reduce((sum, item) => sum + (item.expenses || 0), 0);

    return total;
  };

  const computeTotalExpensesCurrentMonthByHoldings = (data) => {
    return (
      computeTotalExpensesCurrentMonth(data) -
      computeTotalExpensesCurrentMonthByStates(data)
    );
  };

  const computeTotalExpensePreviousMonth = (data) => {
    if (!data.expenses?.expensesmonthpreviousmonth) return 0;
    return data.expenses?.expensesmonthpreviousmonth.reduce(
      (sum, item) => sum + item.expenses,
      0
    );
  };

  const safeArray = (value) => (Array.isArray(value) ? value : []);

  const shortcutimport = data?.expenses ?? {};

  const main = safeArray(shortcutimport?.expensesholdingmonth);
  const noneHolding = safeArray(shortcutimport?.expensesnoneholdingmonthfixed);

  const noneHoldingMap = {};
  noneHolding.forEach((item) => {
    noneHoldingMap[item.year_month] = item?.expenses ?? 0;
  });

  const merged = main.map((item) => {
    const month = item.year_month;
    const revenue = item?.expenses ?? 0;
    const greenIncrease = noneHoldingMap[month] || 0;

    return {
      month,
      revenue,
      greenIncrease,
    };
  });

  const groupedRegion = safeArray(
    shortcutimport.expensesnoneholdingregion
  ).reduce((acc, item) => {
    const { regional, expenses } = item;
    acc[regional] = (acc[regional] || 0) + expenses;
    return acc;
  }, {});

  const colors = ['#F4978E', '#F8AD9D', '#FFDAB9'];
  const totalRevenue = Object.values(groupedRegion).reduce(
    (sum, val) => sum + val,
    0
  );

  const resultArrayRegion = Object.entries(groupedRegion)
    .map(([type, total]) => ({
      name: type,
      value: total,
      percent: totalRevenue
        ? parseFloat(((total / totalRevenue) * 100).toFixed(2))
        : 0,
    }))
    .map((item, index) => ({
      ...item,
      color: item.name === '(None)' ? '#E0E0E0' : colors[index % colors.length],
    }));

  const sumByDepartment = safeArray(
    shortcutimport?.expensesmonthdepartment
  ).reduce((acc, { department, expenses }) => {
    acc[department] = (acc[department] || 0) + expenses;
    return acc;
  }, {});

  const resultArray = Object.entries(sumByDepartment)
    .map(([department, expenses]) => ({
      department,
      expenses,
    }))
    .sort((a, b) => b.expenses - a.expenses);

  const sumByState = (data) =>
    safeArray(data).reduce((acc, item) => {
      acc[item.state] = (acc[item.state] || 0) + item.expenses;
      return acc;
    }, {});

  const sumByMarket = (data) =>
    safeArray(data).reduce((acc, item) => {
      acc[item.market] = (acc[item.market] || 0) + item.expenses;
      return acc;
    }, {});

  const revenueStateArray = Object.entries(
    sumByState(shortcutimport?.expenseshigheststates)
  )
    .map(([state, revenue]) => ({
      state,
      revenue: parseFloat(revenue.toFixed(2)),
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  const revenueMarketArray = Object.entries(
    sumByMarket(shortcutimport?.expenseshighestmarkets)
  )
    .map(([state, revenue]) => ({
      state,
      revenue: parseFloat(revenue.toFixed(2)),
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  const slicedata = data.expenses?.expenseshighestholdingvendors;
  const slicedataNoneHolding = data.expenses?.expenseshighestnoneholdingvendors;

  const groupedHighestHoldingVendor = safeArray(slicedata).reduce(
    (acc, item) => {
      const { name, value, elements } = item;

      // Skip this item if 'elements' is not a valid string
      if (typeof elements !== 'string') return acc;

      let parsedElements;
      try {
        parsedElements = JSON.parse(elements.replace(/'/g, '"'));
      } catch (error) {
        console.warn(`Failed to parse elements for ${name}:`, elements);
        return acc; // Skip this item if JSON parsing fails
      }

      // Initialize or accumulate the total expenses for each vendor
      if (!acc[name]) {
        acc[name] = {
          totalValue: 0,
          elements: {},
        };
      }
      acc[name].totalValue += value;

      // Group elements by 'Name' and sum the 'expenses'
      parsedElements.forEach(({ Name, expenses }) => {
        if (!acc[name].elements[Name]) {
          acc[name].elements[Name] = 0;
        }
        acc[name].elements[Name] += expenses;
      });

      return acc;
    },
    {}
  );

  // Convert object into an array of { name, expenses, elements } objects
  const dataArrayighestHoldingVendor = Object.entries(
    groupedHighestHoldingVendor
  ).map(([name, { totalValue, elements }]) => ({
    state: name,
    revenue: totalValue,
    elements: Object.entries(elements).map(([elementName, totalExpenses]) => ({
      name: elementName,
      value: totalExpenses,
    })),
  }));

  // Sort by expenses descending
  const sortedDataHighestVendors = dataArrayighestHoldingVendor
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  const groupedHighestNoneHoldingVendor = safeArray(
    slicedataNoneHolding
  ).reduce((acc, item) => {
    const { name, value, elements } = item;

    // Skip this item if 'elements' is not a valid string
    if (typeof elements !== 'string') return acc;

    let parsedElements;
    try {
      parsedElements = JSON.parse(elements.replace(/'/g, '"'));
    } catch (error) {
      console.warn(`Failed to parse elements for ${name}:`, elements);
      return acc; // Skip this item if JSON parsing fails
    }

    // Initialize or accumulate the total expenses for each vendor
    if (!acc[name]) {
      acc[name] = {
        totalValue: 0,
        elements: {},
      };
    }
    acc[name].totalValue += value;

    // Group elements by 'Name' and sum the 'expenses'
    parsedElements.forEach(({ Name, expenses }) => {
      if (!acc[name].elements[Name]) {
        acc[name].elements[Name] = 0;
      }
      acc[name].elements[Name] += expenses;
    });

    return acc;
  }, {});

  // Convert object into an array of { name, expenses, elements } objects
  const dataArrayighestNoneHoldingVendor = Object.entries(
    groupedHighestNoneHoldingVendor
  ).map(([name, { totalValue, elements }]) => ({
    state: name,
    revenue: totalValue,
    elements: Object.entries(elements).map(([elementName, totalExpenses]) => ({
      name: elementName,
      value: totalExpenses,
    })),
  }));

  // Sort by expenses descending
  const sortedDataNonetHighestVendors = dataArrayighestNoneHoldingVendor
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  const allHoldingsVendorsHighestExpenses =
    data.expenses?.expenseshighestholdingvendors.flatMap((item) => {
      try {
        const parsed = JSON.parse(item.elements.replace(/'/g, '"'));
        return parsed.map((vendor) => ({
          category: item.name,
          Name: vendor.Name,
          expenses: vendor.expenses,
        }));
      } catch (e) {
        console.error('Parse error:', item.elements);
        return [];
      }
    });

  // Group by Name and sum expenses
  const groupedByHoldingsVendor = Object.values(
    allHoldingsVendorsHighestExpenses.reduce((acc, curr) => {
      if (!acc[curr.Name]) {
        acc[curr.Name] = { Name: curr.Name, expenses: 0 };
      }
      acc[curr.Name].expenses += curr.expenses;
      return acc;
    }, {})
  );

  // Sort descending and take top 5
  const sortedHoldingsVendorsHighestExpenses = groupedByHoldingsVendor
    .sort((a, b) => b.expenses - a.expenses)
    .slice(0, 7);

  const allStatesVendorsHighestExpenses =
    data.expenses?.expenseshighestnoneholdingvendors.flatMap((item) => {
      try {
        const parsed = JSON.parse(item.elements.replace(/'/g, '"'));
        return parsed.map((vendor) => ({
          category: item.name,
          Name: vendor.Name,
          expenses: vendor.expenses,
        }));
      } catch (e) {
        console.error('Parse error:', item.elements);
        return [];
      }
    });

  // Group by Name and sum expenses
  const groupedByStatesVendor = Object.values(
    allStatesVendorsHighestExpenses.reduce((acc, curr) => {
      if (!acc[curr.Name]) {
        acc[curr.Name] = { Name: curr.Name, expenses: 0 };
      }
      acc[curr.Name].expenses += curr.expenses;
      return acc;
    }, {})
  );

  // Sort descending and take top 5
  const sortedStatesVendorsHighestExpenses = groupedByStatesVendor
    .sort((a, b) => b.expenses - a.expenses)
    .slice(0, 7);

  const total = shortcutimport.totalexpensesepicipro.reduce(
    (sum, item) => sum + item.total_expenses,
    0
  );

  const formattedExpenses = shortcutimport.totalexpensesepicipro.map(
    (item, index) => ({
      name: item.realty,
      value: item.total_expenses,
      percent: ((item.total_expenses / total) * 100).toFixed(2),
      color: colors[index % colors.length],
    })
  );

  return (
    <div className='main-frame-expenses'>
      <SubHeader title='EXPENSES' />

      <div className='frame-expenses-block1'>
        <div className='frame-expenses-block1-left'>
          <div className='frame-icon-title'>
            {/* <div className='frame-icon'>
              <img src={iconExpenseDark} alt='icon-expense' />
            </div> */}
            <p>
              {createRevenueCardYearTitle(
                firstRevenueYearMonth,
                lastRevenueYearMonth,
                fromDate,
                toDate
              )}
            </p>
          </div>

          <div className='frame-value-donutchart'>
            <div className='frame-value'>
              <h3>
                {computeTotalExpensesYear(
                  fromDate,
                  toDate,
                  data
                ).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </h3>
              <div className='frame-main-point-title-value-parent'>
              <div className='frame-main-point-title-value'>
                <div className='frame-main-point-title'>
                  <div className='point-holding' />
                  <p>BY HOLDINGS</p>
                </div>

                <p>
                  {' '}
                  {computeTotalExpensesYearByHoldings(
                    fromDate,
                    toDate,
                    data
                  ).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </p>
              </div>

              <div className='frame-main-point-title-value'>
                <div className='frame-main-point-title'>
                  <div className='point-state' />
                  <p>BY STATES</p>
                </div>
                <p>
                  {' '}
                  {computeTotalExpensesYearByStates(
                    fromDate,
                    toDate,
                    data
                  ).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </p>
              </div>
              </div>


            </div>
          </div>
        </div>
        <div className='frame-expenses-block1-left'>
          <div className='frame-icon-title'>
            {/* <div className='frame-icon'>
              <img src={iconExpenseDark} alt='icon-expense' />
            </div> */}
            <p>
              {createRevenueCardQuarterTitle(
                firstRevenueYearMonth,
                lastRevenueYearMonth,
                fromDate,
                toDate
              )}
            </p>
          </div>

          <div className='frame-value-donutchart'>
            <div className='frame-value'>
              <h3>
                {computeTotalExpensesQuarter(
                  fromDate,
                  toDate,
                  data
                ).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </h3>
              <div className='frame-main-point-title-value-parent'>
                <div className='frame-main-point-title-value'>
                  <div className='frame-main-point-title'>
                    <div className='point-holding' />
                    <p>BY HOLDINGS</p>
                  </div>

                  <p>
                    {' '}
                    {computeTotalExpensesQuarterByHoldings(
                      fromDate,
                      toDate,
                      data
                    ).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </p>
                </div>

                <div className='frame-main-point-title-value'>
                  <div className='frame-main-point-title'>
                    <div className='point-state' />
                    <p>BY STATES</p>
                  </div>

                  <p>
                    {' '}
                    {computeTotalExpensesQuarterByStates(
                      fromDate,
                      toDate,
                      data
                    ).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='frame-expenses-block1-right'>
          <div className='frame-expenses-block1-right-top'>
            <TotalExpense
              title={createRevenueCardMonthTitle(
                firstRevenueYearMonth,
                lastRevenueYearMonth,
                fromDate,
                toDate
              )}
              value={computeTotalExpensesCurrentMonth(data)}
              lastMonthValue={computeTotalExpensePreviousMonth(data)}
              image={iconExpenseLight}
            />
          </div>
          <div className='frame-expenses-block1-right-bottom'>
          <LineBarExpense
            dataholdings={computeTotalExpensesCurrentMonthByHoldings(data)}
            datavalue={computeTotalExpensesCurrentMonthByStates(data)}
          />
          </div>
        </div>
      </div>

      <div className='frame-expenses-block2'>
        <h3>TOTAL EXPENSES OF HOLDINGS AND STATES BY MONTHS</h3>
        <div className='frame-main-point-title'>
          <div className='frame-point-title'>
            <div className='frame-circle-holding' />
            <p>BY HOLDINGS</p>
          </div>
          <div className='frame-point-title'>
            <div className='frame-circle-states' />
            <p>BY STATES</p>
          </div>
        </div>
        <StackChartExpense datas={merged} />
      </div>
      <div className='frame-expenses-block3'>
        <div className='frame-expenses-block3-left'>
          <div className='frame-expenses-block3-box-region'>
            <div className='frame-expenses-main-regional'>
              <h3>TOTAL EXPENSES OF STATES BY REGIONAL</h3>
              <div className='frame-regional-expneses-donut'>
                <DonutChartLine data={resultArrayRegion} heights='450px' />
              </div>
            </div>
          </div>
          <div className='frame-expenses-block3-box'>
            <h3>TOP 10 STATES WITH HIGHEST EXPENSES</h3>
            <RevenueBarChart
              height={600}
              firstColor='#FFDAB9'
              secondColor='#F4978E'
              data={revenueStateArray}
            />
          </div>
          <div className='frame-expenses-block3-box'>
            <h3>TOP 10 MARKETS WITH HIGHEST EXPENSES</h3>
            <RevenueBarChart
              height={600}
              firstColor='#FFDAB9'
              secondColor='#F4978E'
              data={revenueMarketArray}
            />
          </div>
        </div>
        <div className='frame-expenses-block3-right'>
          <h3>TOTAL EXPENSES BY DEPARTMENT</h3>
          <ExpenseTable data={resultArray} />
        </div>
      </div>

      <div className='frame-expenses-block4'>
        <div className='frame-expenses-block4-left'>
          <h3>TOP 10 CATEGORIES WITH HIGHEST EXPENSES (HOLDINGS)</h3>
          <ExpenseBarChartHover
            height={800}
            firstColor='#FFDAB9'
            secondColor='#F4978E'
            data={sortedDataHighestVendors}
          />
        </div>
        <div className='frame-expenses-block4-right'>
          <h3>TOP 10 CATEGORIES WITH HIGHEST EXPENSES (STATES)</h3>
          <ExpenseBarChartHover
            height={800}
            firstColor='#FFDAB9'
            secondColor='#F4978E'
            data={sortedDataNonetHighestVendors}
          />
        </div>
      </div>

      <div className='frame-expenses-block5'>
        <div className='frame-expenses-block5-left'>
          <TopVendorsCard
            title='Top 7 Vendors with Highest Expense (Holdings)'
            data={sortedHoldingsVendorsHighestExpenses}
          />
        </div>
        <div className='frame-expenses-block5-right'>
          <TopVendorsCard
            title='Top 7 Vendors with Highest Expense (States)'
            data={sortedStatesVendorsHighestExpenses}
          />
        </div>
      </div>

      <div className='frame-expenses-block6'>
        <div className='frame-expenses-block6-left'>
          <div className='frame-expenses-block6-left-top'>
            <TotalMonthCard
              title={'Total Expenses of Epic Realty'}
              amount={shortcutimport.totalexpensesepicrealty[0].total_expenses}
              previousMonthAmount={
                shortcutimport.totalexpensesepicrealty[0]
                  .last_period_total_expenses
              }
            />
          </div>

          <div className='frame-expenses-block6-left-bottom'>
            <TotalMonthCard
              title={'Total Expenses of iPro Realty'}
              amount={shortcutimport.totalexpensesiprorealty[0].total_expenses}
              previousMonthAmount={
                shortcutimport.totalexpensesiprorealty[0]
                  .last_period_total_expenses
              }
            />
          </div>
        </div>
        <div className='frame-expenses-block6-right'>
          <h3>Total Expenses by Epic Realty & iPro Realty</h3>
          <DonutChartLine data={formattedExpenses} heights='390px' />
        </div>
      </div>

      <div className='frame-expenses-block7'>
        <div className='frame-expenses-block7-left'>
          <h3>Expenses of Epic Realty by Categories</h3>
          <ExpenseTable
            data={shortcutimport.expensesepicbycategory.filter(
              (item) => item.total_expenses > 0
            )}
          />
        </div>
        <div className='frame-expenses-block7-right'>
          <h3>Expenses of iPRo Realty by Categories</h3>
          <ExpenseTable
            data={shortcutimport.expensesiprobycategory.filter(
              (item) => item.total_expenses > 0
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default Expenses;
